import { Stack, Skeleton } from '@mui/material';
import { mixpanelTrack } from 'src/utils/analytics';
import ActivityItem from './ActivityItem';
import { useActivityFeed } from 'src/hooks/query/reportsHooks';
import { ActivityFeedRow, ActivityType } from 'src/@types/reports';
import { useState } from 'react';
import { ReportsProjectFilter } from 'src/@types/project';
import { Range } from 'src/components/date-range-picker';
import { Teammate } from 'src/@types/teammates';
import { ReportsGeographyFilter } from 'src/@types/geographyFilters';
import { ReportsTeamFilter } from 'src/@types/team';
import { TOutreachType } from 'src/@types/outreachType';
import EmptyContent from 'src/components/empty-content/EmptyContent';
import VoterProfileModal from 'src/components/voter-profile/VoterProfileModal';

type Props = {
  filteredOutreachType?: TOutreachType | null;
  filteredUser?: Teammate | Partial<Teammate> | null;
  filteredProject?: ReportsProjectFilter | null;
  filteredDateRange?: Range | null;
  filteredCounty?: ReportsGeographyFilter | null;
  filteredPrecinct?: ReportsGeographyFilter | null;
  filteredCongressionalDistrict?: ReportsGeographyFilter | null;
  filteredStateHouseDistrict?: ReportsGeographyFilter | null;
  filteredStateSenateDistrict?: ReportsGeographyFilter | null;
  filteredDma?: ReportsGeographyFilter | null;
  filteredTeam?: ReportsTeamFilter | null;
  config?: object | null;
};

export default function ActivityFeed({
  filteredOutreachType,
  filteredProject,
  filteredUser,
  filteredDateRange,
  filteredCounty,
  filteredPrecinct,
  filteredCongressionalDistrict,
  filteredStateHouseDistrict,
  filteredStateSenateDistrict,
  filteredDma,
  filteredTeam,
  config = {},
}: Props) {
  const activityFeedQuery = useActivityFeed(
    {
      project_id: filteredProject?.id,
      user_id: filteredUser?.id,
      start_datetime: filteredDateRange?.startDate?.toUTCString(),
      end_datetime: filteredDateRange?.endDate?.toUTCString(),
      county_name: filteredCounty?.id,
      precinct_id: filteredPrecinct?.id,
      congressional_district: filteredCongressionalDistrict?.id,
      state_leg_upper_district: filteredStateSenateDistrict?.id,
      state_leg_lower_district: filteredStateHouseDistrict?.id,
      dma_name: filteredDma?.id,
      team_id: filteredTeam?.id,
      limit: 1000,
      outreach_type: filteredOutreachType || null,
    },
    config || {}
  );
  const activityFeedRows = activityFeedQuery.data ?? [];
  const [selectedRow, setSelectedRow] = useState<ActivityFeedRow | null>(null);

  const handleVoterClick = (row: ActivityFeedRow) => {
    if (
      row.voter_id &&
      row.project_id &&
      (row.type === ActivityType.CALL || row.type === ActivityType.CANVASS)
    ) {
      mixpanelTrack('Opened Voter Profile from Activity Feed', {
        ui_location: 'reports_page',
      });
      setSelectedRow(row);
    }
  };

  const handleCloseModal = () => {
    setSelectedRow(null);
  };

  return (
    <>
      {activityFeedQuery.isFetching && (
        <Stack gap={3.5}>
          <Stack>
            <Skeleton />
            <Skeleton />
            <Skeleton />
          </Stack>
          <Stack>
            <Skeleton />
            <Skeleton />
            <Skeleton />
          </Stack>
          <Stack>
            <Skeleton />
            <Skeleton />
            <Skeleton />
          </Stack>
          <Stack>
            <Skeleton />
            <Skeleton />
            <Skeleton />
          </Stack>
          <Stack>
            <Skeleton />
            <Skeleton />
            <Skeleton />
          </Stack>
        </Stack>
      )}

      {!activityFeedQuery.isFetching && activityFeedRows.length === 0 && (
        <EmptyContent title="No Activity" description="No activity found" />
      )}

      {!activityFeedQuery.isFetching &&
        activityFeedRows.length > 0 &&
        activityFeedRows
          .sort((a: ActivityFeedRow, b: ActivityFeedRow) => {
            const aDate =
              a.paid_interaction_payment_at ||
              a.paid_interaction_verification_submitted_at ||
              a.created_at ||
              new Date().toISOString();
            const bDate =
              b.paid_interaction_payment_at ||
              b.paid_interaction_verification_submitted_at ||
              b.created_at ||
              new Date().toISOString();

            return new Date(bDate).getTime() - new Date(aDate).getTime();
          })
          .map((item, index) => (
            <ActivityItem key={index} activityFeedRow={item} onVoterClick={handleVoterClick} />
          ))}

      {selectedRow && selectedRow.voter_id && selectedRow.project_id && (
        <VoterProfileModal
          open={!!selectedRow}
          onClose={handleCloseModal}
          voterId={selectedRow.voter_id}
          projectId={selectedRow.project_id}
        />
      )}
    </>
  );
}
